// client/src/components/AffiliatesProfil.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, storage } from '../../firebase'; // ✅ cohérent avec les autres pages
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, getDownloadURL, listAll, uploadBytes, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import AffiliatesNavbar from './AffiliatesNavbar';
import { LanguageSelector } from '../../i18n';
import { FaUser, FaCamera, FaDownload, FaExternalLinkAlt, FaSignOutAlt } from 'react-icons/fa';
import { sanitize } from '../../utils/sanitize';
import AccountSwitcher from '../DIVERS/AccountSwitcher';
import { logoutSafely } from '../../utils/logoutSafely';

/* ================================
   Caching & coûts (couche 1, sans changer la fonctionnalité)
================================ */
const PROFILE_PHOTO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours, comme ClientSettings
const CONTRACT_TTL_MS      = 6 * 60 * 60 * 1000;      // 6h: dernier contrat
const SETTINGS_KEY         = 'Settings';
const PROFILE_PHOTO_KEY    = (uid) => `profilePhotoURL_${uid}`;
const CONTRACT_CACHE_KEY   = (uid) => `contract_last_${uid}`; // { url, name, ts }

/* ================================
   Helpers stockage local (safe côté SSR)
================================ */
const safeLocalStorage = (fn, fallback) => {
  try { return fn(); } catch { return fallback; }
};
const fromCache = (key, ttlMs = null) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    if (raw.startsWith('{') || raw.startsWith('[')) {
      const obj = JSON.parse(raw);
      if (!ttlMs) return obj;
      if (!obj || typeof obj !== 'object') return null;
      const ts = typeof obj.ts === 'number' ? obj.ts : 0;
      if (Date.now() - ts > ttlMs) return null;
      return obj;
    }
    return ttlMs ? null : raw;
  } catch {
    return null;
  }
};
const putCache = (key, payload) => {
  try {
    const value = typeof payload === 'string'
      ? payload
      : JSON.stringify({ ...payload, ts: Date.now() });
    localStorage.setItem(key, value);
  } catch {}
};

// Initialisation des Settings depuis le localStorage pour un affichage immédiat
const stored = safeLocalStorage(() => localStorage.getItem(SETTINGS_KEY), null);
const initialSettings = stored
  ? (() => {
      try {
        const parsed = JSON.parse(stored);
        return {
          email: sanitize(parsed.email || ''),
          pseudo: sanitize(parsed.pseudo || ''),
          telephone: sanitize(parsed.telephone || ''),
        };
      } catch {
        return { email: '', pseudo: '', telephone: '' };
      }
    })()
  : { email: '', pseudo: '', telephone: '' };

const AffiliatesProfil = () => {
  const [user, setUser] = useState(initialSettings);
  const [newPseudo, setNewPseudo] = useState('');
  const [showModal, setShowModal] = useState(false);

  const auth = getAuth();
  const currentUid = auth.currentUser?.uid || null;

  const [profilePhotoURL, setProfilePhotoURL] = useState(() => {
    const cached = currentUid
      ? fromCache(PROFILE_PHOTO_KEY(currentUid), PROFILE_PHOTO_TTL_MS)
      : null;
    const url = cached
      ? (typeof cached === 'string' ? cached : cached.url)
      : sanitize(safeLocalStorage(() => localStorage.getItem('profilePhotoURL'), '') || '') || null;
    return url || null;
  });

  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);


  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // --- Récupération des Settings depuis localStorage (au mount) ---
  useEffect(() => {
    const storedSettings = safeLocalStorage(() => localStorage.getItem(SETTINGS_KEY), null);
    if (storedSettings) {
      try {
        const parsed = JSON.parse(storedSettings);
        if (!mountedRef.current) return;
        setUser({
          email: sanitize(parsed.email || ''),
          pseudo: sanitize(parsed.pseudo || ''),
          telephone: sanitize(parsed.telephone || ''),
        });
      } catch (error) {
        console.error('Erreur lors du parsing de Settings :', sanitize(error.message));
      }
    }
  }, []);

  /* ================================
     Photo de profil: cache + refresh silencieux
  ================================= */
  const fetchProfilePhoto = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    // Sert d'abord le cache (si TTL OK)
    const cached = fromCache(PROFILE_PHOTO_KEY(uid), PROFILE_PHOTO_TTL_MS);
    if (cached) {
      const url = typeof cached === 'string' ? cached : cached.url;
      if (url && mountedRef.current) setProfilePhotoURL(url);
    }

    // Refresh Storage silencieux uniquement si pas de cache
    if (!cached && navigator.onLine) {
      try {
        const profilePhotoRef = ref(storage, `${uid}/photo-profil/`);
        const result = await listAll(profilePhotoRef);
        if (!mountedRef.current) return;

        if (!result.items.length) {
          if (mountedRef.current) setProfilePhotoURL(null);
          putCache(PROFILE_PHOTO_KEY(uid), { url: null });
          safeLocalStorage(() => localStorage.removeItem('profilePhotoURL'));
          return;
        }

        // fetch minimal metadata pour déterminer le plus récent
        const itemsWithMetadata = await Promise.all(
          result.items.map(async (item) => {
            const metadata = await getMetadata(item);
            const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0;
            return { item, ts: timeCreated };
          })
        );
        if (!mountedRef.current) return;

        itemsWithMetadata.sort((a, b) => b.ts - a.ts);
        const latestItem = itemsWithMetadata[0].item;
        const url = await getDownloadURL(latestItem);
        if (!mountedRef.current) return;

        const safeUrl = sanitize(url);
        setProfilePhotoURL(safeUrl);
        putCache(PROFILE_PHOTO_KEY(uid), { url: safeUrl }); // reset TTL
        // compat avec ancien stockage
        safeLocalStorage(() => localStorage.setItem('profilePhotoURL', safeUrl));
      } catch (error) {
        console.error('Erreur lors de la récupération de la photo de profil :', sanitize(error.message));
        if (mountedRef.current) setProfilePhotoURL(null);
      }
    }
  }, [auth]);

  // --- Upload d’une nouvelle photo ---
  const handleProfilePhotoChange = async (e) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const file = e.target.files?.[0];
    if (!file) return;

    const fileRef = ref(storage, `${uid}/photo-profil/${Date.now()}_${file.name}`);
    try {
      await uploadBytes(fileRef, file);
      // mise à jour immédiate via URL fraîche + cache
      const url = await getDownloadURL(fileRef);
      if (!mountedRef.current) return;
      const safeUrl = sanitize(url);
      setProfilePhotoURL(safeUrl);
      putCache(PROFILE_PHOTO_KEY(uid), { url: safeUrl });
      safeLocalStorage(() => localStorage.setItem('profilePhotoURL', safeUrl));
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo de profil :', sanitize(error.message));
    }
  };

  // --- Chargement des données Firestore (email/pseudo/téléphone) ---
  useEffect(() => {
    const fetchUserData = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const next = {
            email: sanitize(data.email || ''),
            pseudo: sanitize(data.pseudo || ''),
            telephone: sanitize(data.telephone || ''),
          };
          if (!mountedRef.current) return;
          setUser(next);

          // cache local pour affichage rapide au prochain mount
          safeLocalStorage(() =>
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(next))
          );
        }
      } catch (error) {
        console.error('Erreur lors du chargement des infos utilisateur :', sanitize(error.message));
      }
    };

    fetchUserData();
  }, [auth]);

  // --- Récupération de la photo au montage si connecté (cache-first)
  useEffect(() => {
    if (getAuth().currentUser) {
      fetchProfilePhoto();
    }
  }, [fetchProfilePhoto]);

  // --- Sauvegarde du nouveau pseudo ---
  const handleSavePseudo = async () => {
    const uid = auth.currentUser?.uid;
    const safePseudo = sanitize(newPseudo);

    if (!uid) return alert("Utilisateur non connecté.");
    if (!safePseudo.trim()) return alert("Veuillez entrer un nom d'entreprise valide.");

    try {
      // Mise à jour dans 'users'
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, { pseudo: safePseudo });

      // Mise à jour dans 'code promo/{uid}'
      const codePromoRef = doc(db, 'code promo', uid);
      await setDoc(codePromoRef, { pseudo: safePseudo }, { merge: true });

      if (!mountedRef.current) return;
      setUser((prev) => ({ ...prev, pseudo: safePseudo }));
      setShowModal(false);

      // Réécrit le cache local
      safeLocalStorage(() => {
        const cached = safeLocalStorage(() => localStorage.getItem(SETTINGS_KEY), null);
        const base = cached ? JSON.parse(cached) : {};
        localStorage.setItem(
          SETTINGS_KEY,
          JSON.stringify({ ...base, pseudo: safePseudo })
        );
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du pseudo :', sanitize(error.message));
      alert("Impossible d'enregistrer le pseudo.");
    }
  };

  // --- Déconnexion ---
  const handleLogout = async () => {
    try {
      await logoutSafely(auth);
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', sanitize(error?.message || error));
    } finally {
      try {
        safeLocalStorage(() => {
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userRole');
        });
      } catch {}
      window.location.href = '/';
    }
  };

  // --- Télécharger le dernier contrat signé (cache-first, pas de changement de fonctionnalité) ---
  const handleDownloadLastContract = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return alert('Utilisateur non connecté');

      // Sert d'abord le cache (6h)
      const cached = fromCache(CONTRACT_CACHE_KEY(uid), CONTRACT_TTL_MS);
      if (cached?.url && cached?.name) {
        const resp = await fetch(cached.url, { mode: 'cors', cache: 'no-store' });
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = cached.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return;
      }

      // Sinon: recherche du plus récent puis mise en cache
      const contratRef = ref(storage, `${uid}/documents/contrat/`);
      const res = await listAll(contratRef);
      if (res.items.length === 0) {
        alert('Aucun contrat trouvé.');
        return;
      }

      const itemsWithMetadata = await Promise.all(
        res.items.map(async (item) => {
          const metadata = await getMetadata(item);
          const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(0);
          return { item, timeCreated };
        })
      );
      itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
      const latestItem = itemsWithMetadata[0].item;

      const url = await getDownloadURL(latestItem);
      const resp = await fetch(url, { mode: 'cors', cache: 'no-store' });
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Cache (url + nom)
      putCache(CONTRACT_CACHE_KEY(uid), { url, name: latestItem.name });

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = latestItem.name; // conserve le nom du fichier
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Erreur lors du téléchargement du contrat :', err);
      alert('Impossible de télécharger le contrat.');
    }
  };

  return (
    <div>
      <AffiliatesNavbar />
      <div className="dashboard-container">
        <style>{`
          .ap-page { width: 100%; display: flex; flex-direction: column; gap: 20px; }
          .ap-page .eb-page-header { margin-bottom: 4px; }
          .ap-page .eb-page-title { margin: 0; text-align: left; }
          .ap-identity { padding: 24px; display: flex; align-items: center; gap: 20px; }
          .ap-photo { position: relative; width: 88px; height: 88px; border-radius: 50%; flex-shrink: 0; cursor: pointer; background: var(--eb-surface-2); border: 1px solid var(--eb-border); display: flex; align-items: center; justify-content: center; color: var(--eb-subtle); transition: box-shadow .15s ease; }
          .ap-photo:hover { box-shadow: var(--eb-ring); }
          .ap-photo img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
          .ap-photo__edit { position: absolute; right: -2px; bottom: -2px; width: 30px; height: 30px; border-radius: 50%; background: var(--eb-surface); border: 1px solid var(--eb-border-strong); color: var(--eb-text-2); display: flex; align-items: center; justify-content: center; box-shadow: var(--eb-shadow-sm); font-size: 12px; }
          .ap-identity__hint { margin: 4px 0 0; font-size: 13px; color: var(--eb-muted); }
          .ap-card { text-align: left; height: 100%; }
          .ap-page .eb-grid-2 { align-items: stretch; }
          .dashboard-container .ap-card__title { text-align: left; }
          .ap-card .eb-card__body { padding: 4px 20px; }
          .ap-row { display: flex; align-items: center; justify-content: space-between; gap: 8px 16px; padding: 14px 0; border-bottom: 1px solid var(--eb-border); flex-wrap: wrap; margin: 0; }
          .ap-row:last-child { border-bottom: 0; }
          .ap-row__label { font-size: 14px; font-weight: 600; color: var(--eb-text-2); min-width: 120px; }
          .ap-row__value { display: inline-flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 14.5px; color: var(--eb-text); word-break: break-word; min-width: 0; }
          .ap-links { display: flex; flex-direction: column; }
          .ap-link { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 52px; padding: 0; background: none; border: 0; border-bottom: 1px solid var(--eb-border); color: var(--eb-text); font-size: 14.5px; font-weight: 500; text-align: left; cursor: pointer; }
          .ap-link:last-child { border-bottom: 0; }
          .ap-link:hover { color: var(--eb-brand); }
          .ap-link svg { color: var(--eb-subtle); flex-shrink: 0; }
          .ap-session { padding: 4px 20px; }
          .ap-session .logout-container { width: auto; margin: 0; }
          .ap-session__title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--eb-text); }
          .ap-session__hint { margin: 2px 0 0; font-size: 13px; color: var(--eb-muted); }

          /* Modale */
          .ap-modal-overlay { position: fixed; inset: 0; background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 1000; }
          .ap-modal { background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg); padding: 24px; width: 100%; max-width: 420px; max-height: calc(100dvh - 32px); overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }
          .ap-modal__title { margin: 0; font-size: 19px; font-weight: 700; text-align: left; color: var(--eb-text); }
          .ap-modal__actions { display: flex; justify-content: center; gap: 10px; }

          /* Sélecteur de langue (sans toucher au composant) */
          #language-selector h3 { margin: 0 0 6px; font-size: 13px; font-weight: 500; color: var(--eb-muted); text-align: left; }
          #language-selector select {
            min-height: 40px;
            padding: 0 36px 0 12px;
            border-radius: var(--eb-radius-sm);
            border: 1px solid var(--eb-border-strong);
            background-color: var(--eb-surface);
            font-size: 14px;
            appearance: none;
            -webkit-appearance: none;
            background-image: linear-gradient(45deg, transparent 50%, var(--eb-subtle) 50%),
                              linear-gradient(135deg, var(--eb-subtle) 50%, transparent 50%);
            background-position: calc(100% - 16px) 50%, calc(100% - 11px) 50%;
            background-size: 5px 5px, 5px 5px;
            background-repeat: no-repeat;
          }
          #language-selector button {
            min-height: 36px;
            padding: 0 12px;
            border-radius: var(--eb-radius-sm);
            border: 1px solid var(--eb-border-strong);
            background: var(--eb-surface);
            margin: 0 4px 0 0;
            font-size: 13.5px;
            font-weight: 600;
            color: var(--eb-text-2);
          }
          #language-selector button:hover { background: var(--eb-surface-2); }
          #language-selector .active, #language-selector .selected {
            color: var(--eb-brand);
            border-color: var(--eb-brand-300);
            background: var(--eb-brand-50);
          }

          @media (max-width: 480px) {
            .ap-identity { flex-direction: column; text-align: center; padding: 20px; gap: 14px; }
            .ap-card .eb-card__body, .ap-session { padding: 4px 16px; }
            .ap-session .logout-container, .ap-session .logout-button { width: 100%; justify-content: center; }
            .ap-modal__actions { flex-direction: column-reverse; }
            .ap-modal__actions .eb-btn { width: 100%; }
          }
        `}</style>

        <div className="ap-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Profil</h1>
              <p className="eb-page-subtitle">Vos informations, documents et préférences.</p>
            </div>
          </header>

          {/* Identité */}
          <section className="eb-card ap-identity">
            {/* Photo de profil */}
            <div
              className="profile-photo-container ap-photo"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              title="Changer la photo de profil"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            >
              {profilePhotoURL ? (
                <img src={profilePhotoURL} alt="Profil" className="profile-photo" />
              ) : (
                <FaUser size={36} aria-hidden="true" />
              )}
              <span className="ap-photo__edit" aria-hidden="true"><FaCamera /></span>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleProfilePhotoChange}
                accept="image/*"
              />
            </div>

            <div style={{ minWidth: 0 }}>
              <AccountSwitcher
                label={user?.pseudo || user?.email || 'Mon compte'}
                labelStyle={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--eb-text)' }}
              />
              <p className="ap-identity__hint">Cliquez sur la photo pour la modifier.</p>
            </div>
          </section>

          <div className="eb-grid-2">
            {/* ====== Informations ====== */}
            <section className="eb-card ap-card">
              <div className="eb-card__head">
                <h2 className="eb-card__title ap-card__title">Informations</h2>
              </div>

              <div className="eb-card__body user-info">
                {/* Email */}
                <p className="eb-row ap-row row-email">
                  <span className="ap-row__label">Email</span>
                  <span className="ap-row__value"><span>{user.email || 'Non défini'}</span></span>
                </p>

                {/* Téléphone */}
                <p className="eb-row ap-row row-pseudo">
                  <span className="ap-row__label">Téléphone</span>
                  <span className="ap-row__value"><span>{user.telephone || 'Non défini'}</span></span>
                </p>

                {/* Pseudo + bouton Modifier */}
                <p className="eb-row ap-row row-pseudo">
                  <span className="ap-row__label">Pseudo</span>
                  <span className="ap-row__value">
                    <span>{user.pseudo || 'Non défini'}</span>
                    <button className="eb-btn eb-btn--secondary eb-btn--sm" style={{ minHeight: 36 }} onClick={() => setShowModal(true)}>
                      Modifier
                    </button>
                  </span>
                </p>

                {/* Langue */}
                <div className="eb-row ap-row">
                  <span className="ap-row__label">
                    Langue
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 400, color: 'var(--eb-muted)' }}>Changer la langue de l’interface</span>
                  </span>
                  <div id="language-selector" className="ap-row__value">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </section>

            {/* ====== Documents ====== */}
            <section className="eb-card ap-card">
              <div className="eb-card__head">
                <h2 className="eb-card__title ap-card__title">Documents</h2>
              </div>
              <div className="eb-card__body ap-links">
                {/* Bouton téléchargement dernier contrat signé (cache-first) */}
                <button className="ap-link" onClick={handleDownloadLastContract}>
                  Télécharger le contrat <FaDownload aria-hidden="true" />
                </button>
                <button className="ap-link" onClick={() => window.open('/legal/cgu', '_blank')}>
                  Conditions générales d'utilisation <FaExternalLinkAlt aria-hidden="true" />
                </button>
                <button className="ap-link" onClick={() => window.open('/legal/conditions-affilies', '_blank')}>
                  Conditions affiliés <FaExternalLinkAlt aria-hidden="true" />
                </button>
              </div>
            </section>
          </div>

          {/* Déconnexion */}
          <section className="eb-card ap-session">
            <div className="eb-row">
              <div className="eb-row__main">
                <p className="ap-session__title">Session</p>
                <p className="ap-session__hint">Se déconnecter de ce compte sur cet appareil.</p>
              </div>
              <div className="eb-row__actions logout-container">
                <button className="logout-button" onClick={handleLogout}>
                  <FaSignOutAlt aria-hidden="true" /> Déconnexion
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Modale pseudo */}
        {showModal && (
          <div className="ap-modal-overlay" role="dialog" aria-modal="true">
            <div className="ap-modal">
              <h2 className="ap-modal__title">Nouveau pseudo</h2>
              <input
                type="text"
                className="eb-input"
                value={newPseudo}
                onChange={(e) => setNewPseudo(sanitize(e.target.value))}
                placeholder="Votre nouveau pseudo"
                aria-label="Nouveau pseudo"
              />
              <div className="ap-modal__actions">
                <button className="eb-btn eb-btn--secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button className="pseudo-save-button eb-btn eb-btn--primary" onClick={handleSavePseudo}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default AffiliatesProfil;
